import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';
import { playNotificationSound, showBrowserNotification } from '../../utils/notificationSound';

import API_BASE from '../../utils/api';
const API = API_BASE;

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const prevUnreadRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/communications/my-notifications`, { headers });
      const newNotifications = res.data.notifications || [];
      const newUnread = res.data.unreadCount || 0;

      // Play sound when new unread notifications arrive
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current !== 0) {
        playNotificationSound();
        const diff = newUnread - prevUnreadRef.current;
        showBrowserNotification(
          'New Notification',
          `You have ${diff} new message${diff > 1 ? 's' : ''} from school`
        );
      }
      prevUnreadRef.current = newUnread;

      setNotifications(newNotifications);
      setUnreadCount(newUnread);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Auto-poll every 30 seconds for new notifications
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/communications/${id}/read`, {}, { headers });
      fetchNotifications();
    } catch (err) { console.error(err); }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/communications/mark-all-read`, {}, { headers });
      fetchNotifications();
    } catch (err) { console.error(err); }
  };

  const getChannelIcon = (ch) => {
    switch(ch) {
      case 'sms': return '📱';
      case 'whatsapp': return '💬';
      case 'in_app': return '🔔';
      default: return '📩';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'fee_reminder': case 'fee_overdue': return 'border-l-red-500 bg-red-50';
      case 'attendance': return 'border-l-blue-500 bg-blue-50';
      case 'exam': return 'border-l-purple-500 bg-purple-50';
      case 'event': return 'border-l-green-500 bg-green-50';
      case 'leave': return 'border-l-yellow-500 bg-yellow-50';
      default: return 'border-l-indigo-500 bg-indigo-50';
    }
  };

  const filtered = filter === 'all' ? notifications
    : filter === 'unread' ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.channel === filter);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Notifications</h1>
              <p className="text-gray-500 mt-1">Messages from school about your child</p>
            </div>
            <ProfileDropdown />
          </div>

          {unreadCount > 0 && (
            <div className="flex justify-between items-center bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
              <span className="text-indigo-700 font-medium">You have {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}</span>
              <button onClick={markAllRead} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">Mark all as read</button>
            </div>
          )}

          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { v: 'all', l: 'All' }, { v: 'unread', l: `Unread (${unreadCount})` },
              { v: 'in_app', l: '🔔 In-App' }, { v: 'sms', l: '📱 SMS' }, { v: 'whatsapp', l: '💬 WhatsApp' }
            ].map(f => (
              <button key={f.v} onClick={() => setFilter(f.v)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.v ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
                {f.l}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-4xl mb-2">📭</p>
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(n => (
                <div key={n._id}
                  onClick={() => !n.read && markAsRead(n._id)}
                  className={`border-l-4 rounded-xl p-4 shadow-sm cursor-pointer transition-all hover:shadow-md ${n.read ? 'bg-white border-l-gray-300 opacity-75' : getTypeColor(n.type)}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{getChannelIcon(n.channel)}</span>
                        <h3 className={`font-semibold ${n.read ? 'text-gray-600' : 'text-gray-800'}`}>{n.title}</h3>
                        {!n.read && <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>}
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{n.message}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span>{new Date(n.createdAt).toLocaleString()}</span>
                        {n.sentBy && <span>From: {n.sentBy.name}</span>}
                        {n.studentId && <span>Student: {n.studentId.name}</span>}
                        <span className="capitalize">{n.channel === 'in_app' ? 'In-App' : n.channel}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;